/**
 * Domain types.
 *
 * Mirrors the firmware v4.0 command protocol. A "program" is a chain of up to
 * seven steps; each step holds a frequency, a duration, an intensity, and an
 * optional rest before the next one.
 */

/** One step in a program. The firmware calls these cycles. */
export type CycleSettings = {
  /** How long this step runs, minutes. 1–480. */
  onMin: number;
  /** Rest before the next step, minutes. 0–480. Ignored on the last step. */
  offMin: number;
  /** Carrier frequency, Hz. 100–20000. The firmware always runs one. */
  carrierHz: number;
  /** Field intensity, percent. 0–100. */
  intensity: number;
  /** true = sweep a range, false = hold one frequency. */
  sweepEnabled: boolean;
  /** Held frequency, Hz. 0.5–200. Used when !sweepEnabled. */
  gateHz: number;
  /** Sweep start, Hz. Must be below sweepHi. */
  sweepLo: number;
  /** Sweep end, Hz. Must be above sweepLo. */
  sweepHi: number;
  /** Seconds at each 1 Hz step of a sweep. 1–60. */
  sweepHold: number;
  /** true = sweep up and back down, false = up once then hold. */
  sweepTrue: boolean;
  /**
   * How much of each pulse cycle the field is on, percent.
   *
   * 50 is the original fixed square. Lower values give a brief pulse followed
   * by real silence — 20 means a pulse then four times as long with the coil
   * completely off. Requires firmware with GATEDUTY; older firmware ignores it.
   */
  gateDutyPct: number;
};

/** Library grouping. Organisational only. */
export type Category =
  | 'evidence'
  | 'pain'
  | 'sleep'
  | 'energy'
  | 'immune'
  | 'digestive'
  | 'circulation'
  | 'head'
  | 'traditional';

export const CATEGORY_LABELS: Record<Category, string> = {
  evidence: 'Repair & recovery',
  pain: 'Pain',
  sleep: 'Sleep & calm',
  energy: 'Energy & focus',
  immune: 'Immune & allergy',
  digestive: 'Digestion',
  circulation: 'Circulation',
  head: 'Head, eyes & teeth',
  traditional: 'Nogier series',
};

export const CATEGORY_ORDER: Category[] = [
  'evidence',
  'pain',
  'sleep',
  'energy',
  'immune',
  'digestive',
  'circulation',
  'head',
  'traditional',
];

/** What a program is for. Absent on programs you create yourself. */
export type ProgramInfo = {
  purpose: string;
  origin?: string;
  note?: string;
};

export type Program = {
  id: string;
  /** 1–12 chars, no spaces — the device's own storage limit. */
  name: string;
  /** Human-readable title. */
  title?: string;
  category?: Category;
  cycles: CycleSettings[];
  notes?: string;
  info?: ProgramInfo;
  favorite?: boolean;
  createdAt: number;
  updatedAt: number;
};

/**
 * A frequency range to step over.
 *
 * Sweeps are checked by whether they *cross* a band, not just where they start
 * and end — so a sweep from 8 to 40 Hz is caught for passing through 10 Hz even
 * though it never names it.
 */
export type Band = {
  lo: number;
  hi: number;
  reason?: string;
};

/**
 * A person using the device.
 *
 * Each profile keeps its own programs, favourites, voice history and notes on
 * this phone. Nothing is shared between profiles and nothing leaves the device.
 */
export type Profile = {
  id: string;
  name: string;
  /** Frequencies this person wants skipped. */
  avoidBands: Band[];
  /** Starting pulse/rest ratio for a new step, percent. */
  defaultGateDutyPct?: number;
  /** Starting intensity for a new step, percent. */
  defaultIntensityPct?: number;
  createdAt: number;
};

/** Log entry. Sessions are recorded automatically; notes you write yourself. */
export type LogEntry =
  | {
      kind: 'session';
      at: number;
      programName: string;
      cycles: CycleSettings[];
      totalMin: number;
    }
  | {
      kind: 'note';
      at: number;
      text: string;
    };

/** Live device state, parsed from notifications. */
export type SessionState = {
  running: boolean;
  phase: 'IDLE' | 'TREATMENT' | 'REST';
  cycle: number;
  totalCycles: number;
  timeLeft: number;
  freq: number;
  totalRemaining: number;
};

export const IDLE_SESSION: SessionState = {
  running: false,
  phase: 'IDLE',
  cycle: 0,
  totalCycles: 0,
  timeLeft: 0,
  freq: 0,
  totalRemaining: 0,
};
